import { createMongoAbility, subject, type ForcedSubject, type MongoAbility } from '@casl/ability'
// oxlint-disable-next-line no-restricted-imports
import { Can as CaslCan, useAbility as useCaslAbility, type CanProps } from '@casl/react'
import {
  REAL_ESTATE_RESTRICTED_STATUSES,
  type CurrentAdminRbacPermissionGrant,
  type ResourceAbilityTuple,
} from '@gozoki/api-types'
import type { ReactNode } from 'react'

// Real-estate grants are scoped by project status, so they are checked with object subjects
// (`subject('real-estate', { status })`) instead of the bare string subject. Allow that object
// form alongside the string-subject tuples derived from the RBAC registry.
type RealEstateSubject = { status: string } & ForcedSubject<'real-estate'>

export type AppAbility = MongoAbility<ResourceAbilityTuple | ['read' | 'edit', RealEstateSubject]>

export { subject }

const toRule = (grant: CurrentAdminRbacPermissionGrant) => {
  const base = { action: grant.permissionType, subject: grant.resource }
  if (grant.scope === 'global') return base
  if (grant.resource === 'real-estate' && grant.scope === 'real-estate-restricted') {
    return { ...base, conditions: { status: { $in: REAL_ESTATE_RESTRICTED_STATUSES } } }
  }
  return { ...base, conditions: { scope: grant.scope } }
}

export const createAppAbility = (grants: CurrentAdminRbacPermissionGrant[]): AppAbility =>
  createMongoAbility(grants.map(toRule)) as unknown as AppAbility

export const createAllPowerfulAbility = (): AppAbility =>
  createMongoAbility([{ action: 'manage', subject: 'all' }]) as unknown as AppAbility

export const useAbility = () => useCaslAbility<AppAbility>()

export const Can: (props: CanProps<AppAbility>) => ReactNode = CaslCan
